import React, { useEffect, useState } from 'react';
import BrandLayout from '@/components/BrandLayout';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, IndianRupee, AlertTriangle, TrendingUp, FileText, ArrowRight } from 'lucide-react';

export default function BrandDashboard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get('/brand/dashboard').then(({ data }) => setData(data)).catch(() => {});
    }, []);

    return (
        <BrandLayout>
            <div className="p-10" data-testid="brand-dashboard-page">
                <div className="mb-10">
                    <div className="overline">Overview</div>
                    <h1 className="font-serif text-4xl text-inorbit-blue mt-2">Brand Command Center</h1>
                    <div className="text-sm text-text-secondary mt-1">Live across all Inorbit malls where you operate</div>
                </div>

                {!data ? <div className="text-text-secondary">Loading dashboard…</div> : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="brand-kpi-cards">
                            <KPI label="Live Products" value={data.products.total} icon={Package} />
                            <KPI label="New Orders" value={data.orders.new} icon={ShoppingBag} highlight={data.orders.new > 0} testid="kpi-new-orders" />
                            <KPI label="GMV (lifetime)" value={`₹${Number(data.orders.gmv).toLocaleString('en-IN')}`} icon={IndianRupee} />
                            <KPI label="Out of Stock" value={data.products.out_of_stock} icon={AlertTriangle} highlight={data.products.out_of_stock > 0} />
                            <KPI label="Low Stock" value={data.products.low_stock} icon={AlertTriangle} />
                            <KPI label="Total Orders" value={data.orders.total} icon={ShoppingBag} />
                            <KPI label="Approved Bills" value={data.bills.approved} icon={FileText} />
                            <KPI label="Captured Sales" value={`₹${Number(data.bills.captured_sales || 0).toLocaleString('en-IN')}`} icon={TrendingUp} />
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="md:col-span-2 bg-white border border-border-subtle rounded-md p-8" data-testid="top-products-card">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <div className="overline">Top Sellers</div>
                                        <div className="font-serif text-2xl text-inorbit-blue mt-1">Best-performing SKUs</div>
                                    </div>
                                    <Link to="/brand/products" className="btn-link text-[11px]">All Catalog <ArrowRight size={12} /></Link>
                                </div>
                                {data.top_products?.length ? (
                                    <div className="divide-y divide-border-subtle">
                                        {data.top_products.map((p, i) => (
                                            <div key={p._id} className="flex items-center gap-4 py-3" data-testid={`top-product-${i}`}>
                                                <div className="w-12 h-12 bg-cream overflow-hidden rounded">
                                                    {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-inorbit-blue font-medium truncate">{p.name}</div>
                                                    <div className="text-xs text-text-tertiary">{p.qty} units sold</div>
                                                </div>
                                                <div className="text-sm font-semibold text-inorbit-blue">₹{Number(p.revenue).toLocaleString('en-IN')}</div>
                                            </div>
                                        ))}
                                    </div>
                                ) : <div className="text-sm text-text-tertiary py-8 text-center">No orders yet. Promote your store via Offers & AI Concierge.</div>}
                            </div>

                            <div className="bg-inorbit-blue text-white rounded-md p-8" data-testid="quick-actions-card">
                                <div className="overline text-inorbit-yellow mb-4">Quick Actions</div>
                                <div className="space-y-3">
                                    <Link to="/brand/orders" className="block bg-white/10 hover:bg-white/15 rounded p-4 transition-colors" data-testid="qa-orders">
                                        <div className="font-semibold">Process Orders</div>
                                        <div className="text-xs text-white/70 mt-1">{data.orders.new} new orders waiting</div>
                                    </Link>
                                    <Link to="/brand/inventory" className="block bg-white/10 hover:bg-white/15 rounded p-4 transition-colors" data-testid="qa-inventory">
                                        <div className="font-semibold">Bulk Upload Catalog</div>
                                        <div className="text-xs text-white/70 mt-1">CSV upload — 1000s of SKUs</div>
                                    </Link>
                                    <Link to="/brand/analytics" className="block bg-white/10 hover:bg-white/15 rounded p-4 transition-colors" data-testid="qa-analytics">
                                        <div className="font-semibold">Deep Analytics</div>
                                        <div className="text-xs text-white/70 mt-1">Mall-wise GMV & funnel</div>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </BrandLayout>
    );
}

function KPI({ label, value, icon: Icon, highlight, testid }) {
    return (
        <div className={`bg-white p-6 border rounded-md ${highlight ? 'border-inorbit-orange ring-1 ring-inorbit-orange/30' : 'border-border-subtle'}`} data-testid={testid}>
            <Icon size={18} strokeWidth={1.4} className={highlight ? 'text-inorbit-orange' : 'text-text-tertiary'} />
            <div className="font-serif text-3xl text-inorbit-blue mt-3 break-all">{value}</div>
            <div className="text-[11px] uppercase tracking-widest text-text-tertiary mt-1">{label}</div>
        </div>
    );
}
